import { Spinner, Textarea, cn } from "@heroui/react";
import { Link } from "@remix-run/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { FaArrowRight, FaShuffle } from "react-icons/fa6";
import { FaBolt } from "react-icons/fa";

import { PrimaryButton } from "../PrimaryButton";
import { DEMO_TESTS } from "~/data/demo-tests";
import { setSignupSource, track } from "~/utils/analytics";

type Rating = "weak" | "mid" | "solid" | "savage";
type Style = "playful" | "dominant" | "nonchalant";

type DemoResult = {
  score: number;
  rating: Rating;
  feedback: string;
  betterResponse: string;
  betterResponseStyle: Style;
};

const MAX_CHARS = 240;

const RATING_COPY: Record<Rating, { label: string; color: string }> = {
  weak: { label: "Weak", color: "text-red-400" },
  mid: { label: "Mid", color: "text-amber-400" },
  solid: { label: "Solid", color: "text-emerald-400" },
  savage: { label: "Savage", color: "text-primary" },
};

const STYLE_COPY: Record<Style, string> = {
  playful: "Playful",
  dominant: "Dominant",
  nonchalant: "Nonchalant",
};

// Cycled below the button while the LLM call is in flight, so users see
// the request making progress instead of a silent spinner. Last message
// stays sticky if the call drags past the full cycle. Tuned so a
// "happy path" (~3-5s) sees 2-3 messages; a slow path sees them all.
const LOADING_MESSAGES = [
  "Reading your response…",
  "Scoring your frame…",
  "Drafting feedback…",
  "Finding a sharper line…",
  "Almost there…",
] as const;

const LOADING_CYCLE_MS = 1600;

const pickRandomTest = (exclude?: string): string => {
  const pool = exclude ? DEMO_TESTS.filter((t) => t !== exclude) : DEMO_TESTS;
  return pool[Math.floor(Math.random() * pool.length)];
};

export const InlineDemo = () => {
  // Stable first paint to avoid SSR hydration mismatch — random test picks
  // happen via the shuffle button or after a result, never on mount.
  const [test, setTest] = useState<string>(DEMO_TESTS[0]);
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<DemoResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);

  // Advance the loading message every LOADING_CYCLE_MS while the request
  // is in flight; freeze on the last message so it doesn't loop weirdly
  // if the LLM is slow.
  useEffect(() => {
    if (!isLoading) {
      setLoadingStep(0);
      return;
    }
    const id = setInterval(() => {
      setLoadingStep((prev) =>
        Math.min(prev + 1, LOADING_MESSAGES.length - 1)
      );
    }, LOADING_CYCLE_MS);
    return () => clearInterval(id);
  }, [isLoading]);

  const charsLeft = MAX_CHARS - response.length;
  const overLimit = response.length > MAX_CHARS;

  const submit = async () => {
    if (!response.trim() || overLimit || isLoading) return;
    setIsLoading(true);
    setError(null);
    track("demo_submitted", { test, response_length: response.length });
    try {
      const res = await fetch("/api/demo-evaluate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ test, response }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        setError(data?.error ?? "Something broke. Try again.");
        return;
      }
      const data = (await res.json()) as DemoResult;
      setResult(data);
      track("demo_received", { score: data.score, rating: data.rating });
    } catch (e) {
      console.error(e);
      setError("Network error. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const reset = (rotate: boolean) => {
    setResult(null);
    setResponse("");
    setError(null);
    if (rotate) setTest((current) => pickRandomTest(current));
  };

  return (
    <section className="mt-10">
      <div className="rounded-3xl border-1 border-neutral-800 bg-gradient-to-b from-neutral-900/80 to-neutral-950 overflow-hidden shadow-[0_0_40px_rgba(255,20,126,0.08)]">
        {/* Header strip */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bangers text-base">
            <FaBolt className="text-primary" />
            Try one. No signup.
          </div>
          {!result && (
            <button
              type="button"
              onClick={() => {
                const next = pickRandomTest(test);
                setTest(next);
                setResponse("");
                setError(null);
                track("demo_test_shuffled", { test: next });
              }}
              className="text-xs font-bangers opacity-70 active:opacity-50 inline-flex items-center gap-1.5"
              aria-label="Shuffle test"
            >
              <FaShuffle />
              shuffle
            </button>
          )}
        </div>

        {/* Test bubble */}
        <div className="px-5">
          <ChatBubble text={test} testKey={test} />
        </div>

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key="input"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="px-5 pb-5"
            >
              <Textarea
                aria-label="Your response"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    submit();
                  }
                }}
                placeholder="What do you say back?"
                minRows={3}
                maxRows={6}
                classNames={{
                  inputWrapper:
                    "bg-neutral-900 border-1 border-neutral-700 focus-within:border-primary/60",
                  input: "text-base placeholder:opacity-50",
                }}
              />
              <div
                className={cn(
                  "mt-1.5 flex justify-end text-[10px] font-mono",
                  overLimit ? "text-red-400" : "opacity-50"
                )}
              >
                {charsLeft} chars left
              </div>

              {error && (
                <div className="mt-2 text-xs font-mono text-red-400">
                  {error}
                </div>
              )}

              <div className="mt-3 flex flex-col items-center">
                <PrimaryButton
                  onClick={submit}
                  disabled={!response.trim() || overLimit || isLoading}
                  baseClassName="w-full max-w-xs"
                >
                  {isLoading ? (
                    <Spinner
                      variant="wave"
                      color="white"
                      className="m-0 gap-0"
                    />
                  ) : (
                    "Get Scored"
                  )}
                </PrimaryButton>

                {/* Tight fixed-height slot so the layout doesn't jump
                    between "Free. We don't save..." and the cycling
                    progress copy. */}
                <div className="mt-2 h-4 w-full text-center text-[10px] font-mono opacity-60 leading-4 overflow-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    {isLoading ? (
                      <motion.span
                        key={`loading-${loadingStep}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.18 }}
                        className="inline-block"
                      >
                        {LOADING_MESSAGES[loadingStep]}
                      </motion.span>
                    ) : (
                      <motion.span
                        key="disclaimer"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="inline-block opacity-80"
                      >
                        Free. We don&rsquo;t save your response.
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ) : (
            <ResultPanel result={result} onTryAnother={() => reset(true)} />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

// Renders at 32x32 CSS px. Cloudflare Image Transformations crop + downscale
// the source on the edge so we never ship the 736px Pinterest original.
// width=80 covers up to ~2.5x DPR; format=auto serves WebP/AVIF where supported.
const DEMO_GIRL_PFP_1X =
  "https://cdn.manupgame.com/cdn-cgi/image/width=40,height=40,fit=cover,quality=85,format=auto/landing-pfp.jpg";
const DEMO_GIRL_PFP_2X =
  "https://cdn.manupgame.com/cdn-cgi/image/width=80,height=80,fit=cover,quality=85,format=auto/landing-pfp.jpg";
const DEMO_GIRL_PFP_3X =
  "https://cdn.manupgame.com/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/landing-pfp.jpg";

const ChatBubble = ({ text, testKey }: { text: string; testKey: string }) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={testKey}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.18 }}
      className="flex items-end gap-2 mb-3"
    >
      <img
        src={DEMO_GIRL_PFP_2X}
        srcSet={`${DEMO_GIRL_PFP_1X} 1x, ${DEMO_GIRL_PFP_2X} 2x, ${DEMO_GIRL_PFP_3X} 3x`}
        alt=""
        width={32}
        height={32}
        loading="lazy"
        decoding="async"
        className="w-8 h-8 rounded-full object-cover shrink-0 bg-neutral-800"
      />
      <div className="relative max-w-[85%] rounded-2xl rounded-bl-sm bg-neutral-800 px-4 py-2.5">
        <p className="text-base leading-snug">{text}</p>
      </div>
    </motion.div>
  </AnimatePresence>
);

const ResultPanel = ({
  result,
  onTryAnother,
}: {
  result: DemoResult;
  onTryAnother: () => void;
}) => {
  const ratingMeta = RATING_COPY[result.rating];
  const score = useMemo(
    () => Math.max(0, Math.min(100, Math.round(result.score))),
    [result.score]
  );

  return (
    <motion.div
      key="result"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className="px-5 pb-5"
    >
      {/* Score */}
      <div className="rounded-2xl bg-neutral-900 border-1 border-neutral-800 p-4 flex items-center gap-4">
        <ScoreRing score={score} />
        <div className="flex flex-col">
          <span
            className={cn("font-bangers text-2xl leading-none", ratingMeta.color)}
          >
            {ratingMeta.label}
          </span>
          <span className="font-mono text-xs opacity-70 mt-1 leading-snug">
            {result.feedback}
          </span>
        </div>
      </div>

      {/* Better response */}
      <div className="mt-3 rounded-2xl bg-primary/5 border-1 border-primary/30 p-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-bangers text-sm opacity-80">
            Try this next time
          </span>
          <span className="text-[10px] font-bangers uppercase tracking-wide text-primary">
            {STYLE_COPY[result.betterResponseStyle]}
          </span>
        </div>
        <p className="font-mono text-sm leading-snug">
          &ldquo;{result.betterResponse}&rdquo;
        </p>
      </div>

      {/* Conversion CTA */}
      <div className="mt-5 text-center">
        <p className="font-bangers text-lg leading-tight">
          Want your full skill profile?
        </p>
        <p className="text-xs font-mono opacity-60 mt-1 max-w-xs mx-auto leading-snug">
          Train against 100+ shit tests across Frame, Humor, and Flirt. See
          your weak spots. Free to start.
        </p>
        <div className="mt-4 flex flex-col items-center gap-2">
          <Link
            to="/onboard"
            onClick={() => {
              track("demo_cta_clicked", { rating: result.rating });
              setSignupSource("landing_demo", {
                demo_score: result.score,
                demo_rating: result.rating,
              });
            }}
          >
            <PrimaryButton baseClassName="w-full max-w-xs">
              <span className="inline-flex items-center gap-2">
                Build My Profile <FaArrowRight />
              </span>
            </PrimaryButton>
          </Link>
          <button
            type="button"
            onClick={onTryAnother}
            className="text-xs font-bangers opacity-70 active:opacity-50 mt-1 inline-flex items-center gap-1.5"
          >
            <FaShuffle />
            try another
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ScoreRing = ({ score }: { score: number }) => {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color =
    score >= 81
      ? "stroke-primary"
      : score >= 56
      ? "stroke-emerald-400"
      : score >= 31
      ? "stroke-amber-400"
      : "stroke-red-400";

  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg
        viewBox="0 0 64 64"
        className="-rotate-90 w-full h-full"
        aria-label={`Score ${score} out of 100`}
      >
        <circle
          cx="32"
          cy="32"
          r={radius}
          strokeWidth="5"
          className="stroke-neutral-700 fill-none"
        />
        <motion.circle
          cx="32"
          cy="32"
          r={radius}
          strokeWidth="5"
          strokeLinecap="round"
          className={cn(color, "fill-none")}
          initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-bangers text-xl">
        {score}
      </div>
    </div>
  );
};
